import {
  Card,
  CardContent,
  Divider,
  IconButton,
  Snackbar,
  TextField,
} from '@material-ui/core';

import {
  DevHubButton,
  DevHubModal,
  DevHubTypography,
  DevHubBox,
  DevHubGrid,
  DevHubCard,
} from '@devxdiscover/devhub-ui';
import EditIcon from '@material-ui/icons/Edit';
import SaveIcon from '@material-ui/icons/Save';
import CancelIcon from '@material-ui/icons/Cancel';
import { Fragment, useEffect, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import ExtensibilityQuery from './ExtensibilityQuery';
import UpdateExtension from './UpdateExtension';
import { Alert } from '@material-ui/lab';
import { Extension } from './types/extension';
import { EditableFields } from './types/editableTextFields';
import { Progress } from '@backstage/core-components';

/**
 * This component is used to display the Plugin Stats section of the Settings page.
 * @constructor
 */
export const PluginStats = () => {
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
  const [plugins, setPlugins] = useState<Extension[]>([]);
  const [editableFields, setEditableFields] = useState<EditableFields>({});
  const [currentExtensionId, setCurrentExtensionId] = useState<string>('');
  const [selectedPlugin, setSelectedPlugin] = useState<Extension>(
    {} as Extension,
  );
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { data, loading, error } = useQuery(ExtensibilityQuery);
  const [updateExtensionMutation] = useMutation(UpdateExtension);
  useEffect(() => {
    if (!loading && !error && data) {
      const filtered = data?.devhub?.extensions.filter(
        (extension: Extension) => extension.extensionType === 'Plugin',
      );
      setPlugins(filtered);
    }
  }, [data, loading, error]);

  const handleEdit = (extensionId: string) => {
    const pluginIndex = plugins.findIndex(plugin => plugin.id === extensionId);

    if (pluginIndex === -1) {
      return;
    }

    setCurrentExtensionId(extensionId);
    // Set the editable fields based on the existing template or default values
    const pluginToEdit = plugins[pluginIndex];
    setEditableFields(prevEditableFields => ({
      ...prevEditableFields,
      [extensionId]: {
        distributionList: pluginToEdit.distributionList || '',
        extensionType: pluginToEdit.extensionType || '',
        name: pluginToEdit.name || '',
        notes: pluginToEdit.notes || '',
        owner: pluginToEdit.owner || '',
        securityCredentialsUrl: pluginToEdit.securityCredentialsUrl || '',
        slackChannel: pluginToEdit.slackChannel || '',
      },
    }));
  };

  const handleCancel = () => {
    setEditableFields(prevEditableFields => {
      const updatedFields = { ...prevEditableFields };
      delete updatedFields[currentExtensionId];
      return updatedFields;
    });
    setCurrentExtensionId('');
  };

  const handleOpen = (plugin: Extension) => {
    setSelectedPlugin(plugin);
    setModalIsOpen(true);
  };

  const handleClose = () => {
    handleCancel();
    setModalIsOpen(false);
  };

  const handleChange = (pluginId: string, field: string, value: string) => {
    setEditableFields(prevEditableFields => ({
      ...prevEditableFields,
      [pluginId]: {
        ...prevEditableFields[pluginId],
        [field]: value,
      },
    }));
  };

  const handleSave = () => {
    if (!currentExtensionId) {
      return;
    }

    const pluginIndex = plugins.findIndex(
      plugin => plugin.id === currentExtensionId,
    );

    if (pluginIndex === -1) {
      return;
    }

    const pluginToEdit = editableFields[currentExtensionId];

    // Call the Fusion mutation to update the template with the new values
    updateExtensionMutation({
      variables: {
        input: {
          extensionId: currentExtensionId,
          ...pluginToEdit,
        },
      },
    }).then(() => {
      setSuccessMessage('Plugin data successfully saved!');
    });

    setEditableFields(prevEditableFields => {
      const updatedFields = { ...prevEditableFields };
      delete updatedFields[currentExtensionId];
      return updatedFields;
    });

    setCurrentExtensionId('');
    setModalIsOpen(false);
  };

  return (
    <>
      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>
      {loading && <Progress />}
      <DevHubGrid container>
        {plugins
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(plugin => (
            <Fragment key={plugin?.id}>
              <DevHubGrid item xs={12} md={6}>
                <DevHubCard
                  density="compact"
                  title={plugin?.name}
                  actionItem={
                    <DevHubGrid container margin="xsmall">
                      <DevHubButton
                        buttonColor="primary"
                        variant="outlined"
                        onClick={() => handleOpen(plugin)}
                      >
                        Open
                      </DevHubButton>
                    </DevHubGrid>
                  }
                  subTitle={plugin?.owner}
                />
              </DevHubGrid>

              <DevHubModal open={modalIsOpen} onClose={handleClose}>
                <Fragment>
                  <DevHubBox sx={{ display: 'flex', margin: '.5rem' }}>
                    <DevHubTypography variant="h6">
                      {selectedPlugin?.name}
                    </DevHubTypography>
                    {editableFields[selectedPlugin?.id] ? (
                      <>
                        <IconButton onClick={handleSave}>
                          <SaveIcon />
                        </IconButton>
                        <IconButton onClick={handleCancel}>
                          <CancelIcon />
                        </IconButton>
                      </>
                    ) : (
                      <IconButton
                        onClick={() => handleEdit(selectedPlugin?.id)}
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                  </DevHubBox>
                  {editableFields[selectedPlugin?.id] ? (
                    // Show the editable text fields when in edit mode
                    <Card>
                      <CardContent>
                        <TextField
                          label="Name"
                          value={editableFields[selectedPlugin?.id].name}
                          onChange={e =>
                            handleChange(
                              selectedPlugin?.id,
                              'name',
                              e.target.value,
                            )
                          }
                          fullWidth
                        />
                        <TextField
                          label="Owner (Github Group)"
                          value={editableFields[selectedPlugin?.id].owner}
                          onChange={e =>
                            handleChange(
                              selectedPlugin?.id,
                              'owner',
                              e.target.value,
                            )
                          }
                          fullWidth
                        />
                        <TextField
                          label="Distribution List"
                          value={
                            editableFields[selectedPlugin?.id].distributionList
                          }
                          onChange={e =>
                            handleChange(
                              selectedPlugin?.id,
                              'distributionList',
                              e.target.value,
                            )
                          }
                          fullWidth
                        />
                        <TextField
                          label="Slack Channel"
                          value={
                            editableFields[selectedPlugin?.id].slackChannel
                          }
                          onChange={e =>
                            handleChange(
                              selectedPlugin?.id,
                              'slackChannel',
                              e.target.value,
                            )
                          }
                          fullWidth
                        />
                        <TextField
                          label="Notes"
                          value={editableFields[selectedPlugin?.id].notes}
                          onChange={e =>
                            handleChange(
                              selectedPlugin?.id,
                              'notes',
                              e.target.value,
                            )
                          }
                          fullWidth
                        />
                        <TextField
                          label="Security Credentials (URL to Vault)"
                          value={
                            editableFields[selectedPlugin?.id]
                              .securityCredentialsUrl
                          }
                          onChange={e =>
                            handleChange(
                              selectedPlugin?.id,
                              'securityCredentialsUrl',
                              e.target.value,
                            )
                          }
                          fullWidth
                        />
                        <TextField
                          label="Type (Plugin/Template)"
                          value={
                            editableFields[selectedPlugin?.id].extensionType
                          }
                          onChange={e =>
                            handleChange(
                              selectedPlugin?.id,
                              'extensionType',
                              e.target.value,
                            )
                          }
                          fullWidth
                        />
                      </CardContent>
                    </Card>
                  ) : (
                    // Show the existing plugin data in a card when not in edit mode
                    <Card>
                      <CardContent>
                        <DevHubTypography variant="body2">
                          Owner (Github Group):
                        </DevHubTypography>
                        <DevHubTypography variant="body1">
                          {selectedPlugin?.owner}
                        </DevHubTypography>
                        <Divider />
                        <DevHubTypography variant="body2">
                          Distribution List:
                        </DevHubTypography>
                        <DevHubTypography variant="body1">
                          {selectedPlugin?.distributionList}
                        </DevHubTypography>
                        <Divider />
                        <DevHubTypography variant="body2">
                          Slack Channel:
                        </DevHubTypography>
                        <DevHubTypography variant="body1">
                          {selectedPlugin?.slackChannel}
                        </DevHubTypography>
                        <Divider />
                        <DevHubTypography variant="body2">
                          Notes:
                        </DevHubTypography>
                        <DevHubTypography variant="body1">
                          {selectedPlugin?.notes}
                        </DevHubTypography>
                        <Divider />
                        <DevHubTypography variant="body2">
                          Security Credentials (URL to Vault):
                        </DevHubTypography>
                        <DevHubTypography variant="body1">
                          <a
                            href={selectedPlugin?.securityCredentialsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {selectedPlugin?.securityCredentialsUrl}
                          </a>
                        </DevHubTypography>
                        <Divider />
                        <DevHubTypography variant="body2">
                          Type (Plugin/Template):
                        </DevHubTypography>
                        <DevHubTypography variant="body1">
                          {selectedPlugin?.extensionType}
                        </DevHubTypography>
                        <Divider />
                      </CardContent>
                    </Card>
                  )}
                </Fragment>
              </DevHubModal>
            </Fragment>
          ))}
      </DevHubGrid>
    </>
  );
};
